import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { ShieldCheck, Database, Wrench } from 'lucide-react';
export function WhyLAI() {
  const reasons = [{
    icon: Wrench,
    title: 'Engineers First, Tech Second',
    description: "We aren't generic IT consultants. We understand RFI loops, transmittals, and ITPs because we come from industry. We solve the actual problem, not just the technical one.",
    metric: '100%',
    metricLabel: 'Engineering Background'
  }, {
    icon: Database,
    title: 'We Work Inside Your Microsoft Environment',
    description: 'No new software licences. No shadow IT. We leverage the SharePoint, Power Automate, and Teams infrastructure you already pay for, ensuring governance and security.',
    metric: '$0',
    metricLabel: 'New Software Cost'
  }, {
    icon: ShieldCheck,
    title: 'Systems That Actually Work',
    description: "We build robust, practical workflows that handle the messiness of real projects. If it doesn't save time or reduce risk, we don't build it.",
    metric: '99.8%',
    metricLabel: 'System Uptime'
  }];
  return <section className="relative overflow-hidden bg-dashboard-bg py-16 md:py-24 border-b border-dashboard-border" data-id="element-264">
      <Container data-id="element-265">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch" data-id="element-266">
          {/* Visual representation - will match height of right column */}
          <motion.div className="order-2 lg:order-1 flex flex-col" initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-267">
            <div className="flex-1 rounded-xl bg-dashboard-surface overflow-hidden relative border border-dashboard-border shadow-2xl" data-id="element-268">
              {/* Abstract workflow visualisation */}
              <div className="absolute inset-0 p-8 flex items-center justify-center" data-id="element-269">
                <div className="w-full max-w-md bg-dashboard-bg rounded-lg border border-dashboard-border p-6" data-id="element-270">
                  {/* Browser chrome */}
                  <div className="h-10 bg-dashboard-surface border-b border-dashboard-border flex items-center px-4 space-x-2 -mx-6 -mt-6 mb-6" data-id="element-271">
                    <div className="w-3 h-3 rounded-full bg-tech-red" data-id="element-272"></div>
                    <div className="w-3 h-3 rounded-full bg-tech-yellow" data-id="element-273"></div>
                    <div className="w-3 h-3 rounded-full bg-tech-green" data-id="element-274"></div>
                  </div>
                  {/* Content */}
                  <div className="space-y-4" data-id="element-275">
                    <div className="h-3 bg-dashboard-border rounded w-3/4" data-id="element-276"></div>
                    <div className="h-3 bg-tech-blue/20 rounded w-1/2" data-id="element-277"></div>
                    <div className="h-40 bg-dashboard-surface rounded-lg border border-tech-blue/30 flex items-center justify-center text-tech-blue text-sm font-semibold shadow-inner" data-id="element-278">
                      <div className="text-center" data-id="element-279">
                        <div className="mb-2" data-id="element-280">Microsoft 365 Integration</div>
                        <div className="text-xs text-dashboard-muted" data-id="element-281">
                          SharePoint • Power Automate • Teams
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between gap-3" data-id="element-282">
                      <div className="h-8 bg-tech-blue rounded flex-1 shadow-md" data-id="element-283"></div>
                      <div className="h-8 bg-dashboard-border rounded flex-1" data-id="element-284"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content - right column */}
          <motion.div className="order-1 lg:order-2 flex flex-col" initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-285">
            <div className="mb-6" data-id="element-286">
              <span className="text-xs text-tech-blue uppercase tracking-wider" data-id="element-287">
                // Differentiators
              </span>
            </div>
            <h2 className="text-4xl font-black tracking-tight text-dashboard-text sm:text-5xl mb-8" data-id="element-288">
              Why Engineering Leaders{' '}
              <span className="text-tech-blue" data-id="element-289">Trust LAI</span>
            </h2>

            <div className="space-y-6 flex-1 flex flex-col" data-id="element-290">
              {reasons.map((reason, index) => <motion.div key={index} className="bg-dashboard-surface rounded-lg p-6 border border-dashboard-border flex-1" initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1,
              duration: 0.5
            }} data-id="element-291">
                  <div className="flex items-start gap-4" data-id="element-292">
                    <div className="flex-shrink-0 mt-1" data-id="element-293">
                      <div className="h-12 w-12 rounded-lg bg-tech-blue/10 border border-tech-blue/30 flex items-center justify-center" data-id="element-294">
                        <reason.icon className="h-6 w-6 text-tech-blue" data-id="element-295" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-296">
                      <div className="flex items-start justify-between mb-2" data-id="element-297">
                        <h3 className="text-lg font-bold text-dashboard-text" data-id="element-298">
                          {reason.title}
                        </h3>
                        <div className="text-right ml-4" data-id="element-299">
                          <div className="text-xl font-black text-tech-green" data-id="element-300">
                            {reason.metric}
                          </div>
                          <div className="text-xs text-dashboard-muted" data-id="element-301">
                            {reason.metricLabel}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-dashboard-muted leading-relaxed" data-id="element-302">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </Container>
    </section>;
}